'use client'

import { Section, SectionHeading } from '@/components/platform/v2/Section'
import { ProductHero, Statement, FlowStrip, FeatureCard, NextSteps, ProductCta, RelatedCases } from '@/components/platform/v2/ProductKit'
import { ChatMock, SegmentMock } from '@/components/platform/v2/Mocks'

const GROUP_FEATURES: { title: string; text: string }[] = [
    { title: 'Личные чаты с клиентом', text: 'Диалог один на один: менеджер видит клиента и его историю.' },
    { title: 'Групповые чаты', text: 'Обсуждение внутри команды или закрытой группы клиентов.' },
    { title: 'Каналы', text: 'Односторонние публикации для аудитории проекта.' },
    { title: 'Сообщества', text: 'Пространства с участниками, материалами и активностями.' },
    { title: 'Модерация и роли', text: 'Права участников, ограничения, ответственные за общение.' },
    { title: 'AI-помощник', text: 'Отвечает на частые вопросы и передаёт диалог менеджеру.' },
]

const PUSH = [
    'Напоминание о визите',
    'Изменение статуса заказа',
    'Новое предложение',
    'Сообщение менеджера',
    'Событие или мероприятие',
    'Баллы начислены',
]

const CommunicationsPage = () => {
    return (
        <div className="vcentre-public pt-24 sm:pt-28 md:pt-32">
            <ProductHero
                eyebrow="Коммуникации"
                titleLines={['Разговор с клиентом', 'не должен теряться', 'между сервисами.']}
                accentLine={2}
                lead="Чаты, push, рассылки и другие каналы коммуникации становятся частью общего клиентского контекста."
                primary={{ label: 'Собрать коммуникации под мой бизнес', to: '/signin?focus=communications' }}
                secondary={{ label: 'CRM и клиенты', to: '/platform/crm' }}
                visual={
                    <img
                        src={'/assets/images/platform/communications-mascot-hero.png'}
                        alt="Маскот VCENTRE.IT с телефоном и фразой «Всегда на связи»"
                        width={1254}
                        height={1254}
                        loading="eager"
                        className="w-full max-w-[330px] animate-float drop-shadow-[0_30px_60px_rgba(0,0,0,0.6)] sm:max-w-[420px]"
                    />
                }
            />

            {/* СОБСТВЕННЫЕ ЧАТЫ */}
            <Section tone="quiet">
                <SectionHeading
                    align="left"
                    eyebrow="Свои чаты"
                    title="Не чат отдельно. Клиент рядом."
                    text="Менеджер отвечает и одновременно видит, кто это, что человек покупал и на каком этапе его сделка."
                />
                <div className="mt-8 flex flex-col gap-6">
                    <ChatMock />
                    <FlowStrip
                        steps={[
                            { title: 'Клиент пишет' },
                            { title: 'Менеджер видит клиента', note: 'профиль и история' },
                            { title: 'Видит его CRM', note: 'заявки, сделки, заказы' },
                            { title: 'Открывает сделку', accent: true },
                            { title: 'Продолжает разговор' },
                        ]}
                        compact
                    />
                </div>
            </Section>

            {/* ГРУППЫ И КАНАЛЫ */}
            <Section>
                <SectionHeading align="left" eyebrow="Форматы общения" title="От личного диалога до сообщества" />
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {GROUP_FEATURES.map((f) => (
                        <div key={f.title} className="surface flex flex-col gap-2 text-center">
                            <h3 className="text-lg font-bold text-foreground sm:text-xl">{f.title}</h3>
                            <p className="body-text">{f.text}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* PUSH */}
            <Section tone="quiet">
                <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                    <div className="flex flex-col gap-4">
                        <p className="eyebrow">Push-уведомления</p>
                        <h2 className="display-2">Сообщение приходит вовремя</h2>
                        <p className="lede">
                            Уведомление отправляется не «по расписанию маркетолога», а по событию: клиент записался, оплатил, получил баллы,
                            изменился статус заказа.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        {PUSH.map((p) => (
                            <div
                                key={p}
                                className="rounded-xl border border-border/60 bg-[hsl(var(--deep-black))] px-4 py-3 text-sm font-semibold text-foreground/90"
                            >
                                {p}
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* РАССЫЛКИ */}
            <Section>
                <SectionHeading
                    align="left"
                    eyebrow="Рассылки"
                    title="Не «отправить всей базе»"
                    text="Рассылка работает, когда она связана с данными клиента: что покупал, когда был в последний раз, чем интересовался."
                />
                <div className="mt-8 flex flex-col gap-6">
                    <SegmentMock />
                    <div className="grid gap-4 md:grid-cols-3">
                        <FeatureCard title="Сегменты" text="Выборка по действиям, покупкам и активности клиента." />
                        <FeatureCard title="Свой почтовый ящик" text="Подключение собственного email для отправки." />
                        <FeatureCard title="Результат в CRM" text="Ответ и заявка после рассылки видны в карточке клиента." />
                    </div>
                </div>
            </Section>

            {/* ВНЕШНИЕ КАНАЛЫ */}
            <Section tone="quiet">
                <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-center">
                    <div className="flex flex-col gap-4">
                        <p className="eyebrow">Внешние каналы</p>
                        <h2 className="display-2">Подключаются, но не заменяют систему</h2>
                        <p className="lede">
                            Мессенджеры и другие внешние каналы — дополнительные точки контакта. Основа коммуникаций остаётся внутри вашего
                            контура, поэтому история не пропадает при смене площадки или менеджера.
                        </p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <div className="surface-quiet">
                            <p className="text-sm font-bold text-foreground">Свои каналы</p>
                            <p className="mt-1 text-sm text-muted-foreground">Чат в приложении, push, email, личный кабинет.</p>
                        </div>
                        <div className="surface-quiet">
                            <p className="text-sm font-bold text-foreground">Внешние каналы</p>
                            <p className="mt-1 text-sm text-muted-foreground">Мессенджеры и внешние сервисы как дополнение.</p>
                        </div>
                    </div>
                </div>
            </Section>

            <Section>
                <Statement>Чат, клиент и сделка находятся рядом.</Statement>
            </Section>

            <Section tone="quiet">
                <RelatedCases
                    slugs={['dobrodomik', 'business-forum']}
                    text="Сообщества и мероприятия, где общение — главная часть продукта."
                />
            </Section>

            <Section>
                <NextSteps
                    items={[
                        { to: '/platform/automation-ai', text: 'Автоматизируйте следующие действия после сообщения' },
                        { to: '/platform/crm', text: 'Посмотрите, где хранится история переписки' },
                    ]}
                />
            </Section>

            <Section tone="quiet">
                <ProductCta
                    title="Покажите, как сейчас работает ваш бизнес"
                    text="Мы покажем, как собрать все каналы общения вокруг клиента в VCENTRE.ONE."
                    buttonLabel="Собрать коммуникации"
                    to="/signin?focus=communications&source=communications-bottom"
                    secondary={{ to: '/platform', label: 'Обзор платформы' }}
                    visual={
                        <img
                            src={'/assets/images/platform/communications-mascot-cta.png'}
                            alt="Маскот VCENTRE.IT с ракетой и фразой «Запускаем быстро»"
                            width={1254}
                            height={1254}
                            loading="lazy"
                            className="w-full max-w-[260px] drop-shadow-[0_24px_48px_rgba(0,0,0,0.55)]"
                        />
                    }
                />
            </Section>
        </div>
    )
}

export default CommunicationsPage
